<template>
  <section
    class="invoice-preview-wrapper"
    style="position: relative"
    aria-label="Run sheet detail"
    role="main"
  >
    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="roadmap === undefined"
    >
      <h4 class="alert-heading">
        Erreur lors de la récupération des données de la run sheet
      </h4>
      <div class="alert-body">
        Aucune run sheet trouvée avec cet identifiant. Vérifiez la liste des run sheets pour d'autres éléments.
      </div>
    </b-alert>
    <b-row
      v-if="roadmap"
      class="invoice-preview"
    >
      <!-- Col: Left (Invoice Container) -->
      <b-col
        cols="12"
        xl="9"
        md="8"
      >
        <b-card
          v-if="roadmap"
          no-body
          class="invoice-preview-card"
        >
          <!-- Header -->
          <div
            class="d-flex justify-content-center flex-md-row flex-column invoice-spacing mt-0"
          >
            <div style="color: black">
              <div>
                <img
                  :src="`${publicBaseUrl}apple-touch-icon.png`"
                  width="250px"
                  height="100%"
                  alt="Logo de la société"
                >
              </div>
            </div>
          </div>

          <div class="d-flex flex-row justify-content-between align-items-start mb-4">
            <div class="ml-3">
              <h1
                id="roadmap-detail-title"
                style="color: black"
                aria-label="Titre de la run sheet"
              >Run sheet N° : {{ roadmap.id }}</h1>
            </div>
            <div class="mr-3">
              <h4 style="color: black; font-weight: bold; margin-top: 18px;">
                Date : {{ roadmap.date }}
              </h4>
            </div>
          </div>

          <!-- Two Cards Side by Side -->
          <b-card-body class="px-3">
            <div class="d-flex flex-row justify-content-between mb-4 cards-container">
              <!-- Company Card -->
              <div class="info-card company-card">
                <div
                  class="h3"
                  style="color:black"
                >
                  <strong>{{ company.name }}</strong> : {{ company.tagline || 'Société de livraison à domicile' }}
                </div>
                <div
                  class="h3"
                  style="color:black"
                >
                  <strong>Matricule fiscale :</strong> {{ company.tax_identification_number }}
                </div>
                <div
                  class="h3"
                  style="color:black"
                >
                  <strong>Adresse :</strong> {{ company.address }}
                </div>
                <div
                  class="h3"
                  style="color:black"
                >
                  <strong>Tel :</strong> {{ company.phone1 }}
                </div>
              </div>

              <!-- Delivery Man Card -->
              <div
                v-if="roadmap.delivery_man"
                class="info-card delivery-card"
              >
                <div
                  class="h3"
                  style="color:black"
                >
                  <strong>Livreur :</strong> {{ roadmap.delivery_man.username }}
                </div>
                <div
                  class="h3"
                  style="color:black"
                >
                  <strong>Tel livreur :</strong> {{ roadmap.delivery_man.phone }}
                </div>
                <div
                  v-if="roadmap.delivery_man.car"
                  class="h3"
                  style="color:black"
                >
                  <strong>Matricule voiture :</strong> {{ roadmap.delivery_man.car.serial_number }}
                </div>
                <div
                  class="h3"
                  style="color:black; font-size: 1.5rem; font-weight: bold;"
                >
                  <strong>Nombre des colis :</strong> {{ orders.length }}
                </div>
              </div>
            </div>
          </b-card-body>

          <!-- Invoice Description: Table -->
          <b-card-body class="invoice-padding pt-0">
            <b-table
              class="ta"
              responsive
              :items="orders"
              :fields="columns"
              aria-label="Tableau des colis de la run sheet"
              caption-top
            >
              <template #cell(code)="data">
                <div class="code-cell">
                  <!-- <qrcode-vue
                    :value="data.item.code.toString()"
                    :size="size"
                    level="H"
                  /> -->
                  <span aria-label="Code du colis">{{ data.item.code }}</span>
                </div>
              </template>
              <template #cell(fournisseur)="data">
                <div class="fournisseur-cell">
                  <strong
                    style="font-size: 20px"
                    aria-label="Fournisseur"
                  >
                    {{ data.item.external_supplier_name !== null ? data.item.external_supplier_name : data.item.fournisseur }}
                  </strong>
                  <strong
                    v-if="data.item.external_supplier_phone === null"
                    style="font-size: 20px"
                    class="d-block"
                    aria-label="Téléphone fournisseur"
                  >{{ maskPhone(data.item.telFournisseur) }} <br> {{ maskPhone(data.item.telFournisseur2) }}</strong>
                  <strong
                    v-else
                    style="font-size: 20px"
                    class="d-block"
                    aria-label="Téléphone fournisseur externe"
                  >{{ maskPhone(data.item.external_supplier_phone) }}</strong>
                </div>
              </template>
              <template #cell(destinataire)="data">
                <div class="destinataire-cell">
                  <strong
                    style="font-size: 20px"
                    aria-label="Destinataire"
                  >{{ data.item.destinataire }}</strong>
                  <strong
                    style="font-size: 20px"
                    class="d-block"
                    aria-label="Téléphone destinataire"
                  >{{ data.item.tel }} <br>
                    {{ data.item.tel2 }}
                  </strong>
                </div>
              </template>
              <template #cell(gouvernorat)="data">
                <div class="gouvernorat-cell">
                  <strong
                    style="font-size: 20px"
                    aria-label="Adresse"
                  >
                    {{ data.item.gouvernorat }}<span v-if="data.item.delegation">, {{ data.item.delegation }}</span>
                  </strong>
                  <strong
                    style="font-size: 20px"
                    class="d-block"
                    aria-label="Adresse"
                  >{{ data.item.adresse }}</strong>
                </div>
              </template>
              <template #cell(info)="data">
                <div class="info-cell">
                  <span aria-label="Informations">{{ data.item.info }}</span>
                </div>
              </template>
              <template #cell(prix)="data">
                <div class="prix-cell">
                  <span aria-label="Prix">{{ data.item.prix }}</span>
                  <span
                    v-if="data.item.is_an_exchange"
                    style="color: #EA2F2F !important; font-weight: bold !important; font-size: 30px; display: block; margin-top: 5px; text-align: center;"
                  >
                    Echange
                  </span>
                  <span
                    v-if="data.item.can_open_package"
                    style="color: #C59C6C !important; font-weight: bold !important; font-size: 30px; display: block; margin-top: 5px; text-align: center;"
                  >
                    Ouvert
                  </span>
                </div>
              </template>
              <!-- <template #cell(last_return_reason)="data">
                <div class="return-reason-cell">
                  <span aria-label="Dernier motif de retour">
                    {{ (data.item.last_return_reason && typeof data.item.last_return_reason === 'object') ? data.item.last_return_reason.return_reason : (data.item.last_return_reason || '') }}
                  </span>
                </div>
              </template> -->
              <template #cell(action)="data">
                <div class="action-cell">
                  <span aria-label="Action">{{ data.item.action }}</span>
                </div>
              </template>
              <template #cell(return_number)="data">
                <div class="return-number-cell">
                  <span aria-label="Retour">Retour : {{ data.item.return_number }}</span>
                </div>
              </template>
            </b-table>
            <b-card-body class="d-flex justify-content-between mt-5">
              <!-- Col: Total -->
              <div class="mb-5 ml-1">
                <h3 style="color: black; font-size: 40px;font-weight: bold;">
                  Signature
                </h3>
              </div>
              <div>
                <h1 style="color: black; font-size: 40px;font-weight: bold;">
                  Total : {{ total.toFixed(3) }} DT
                </h1>
              </div>
            </b-card-body>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions"
      >
        <b-card>
          <!-- Button: Sort by Location -->
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="outline-primary"
            class="mb-75"
            block
            @click="sortByGovernorate"
          >
            Trier par localisation
          </b-button>
          <!-- Button: Print -->
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="printInvoice"
          >
            Imprimer
          </b-button>
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import { ref } from '@vue/composition-api'
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BTable,
  BButton,
  BAlert,
  VBToggle,
} from 'bootstrap-vue'

import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
// import QrcodeVue from 'qrcode.vue'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTable,
    BButton,
    BAlert,
    // QrcodeVue,
  },
  data() {
    return {
      p: [],
      roadmap: [],
      orders: [],
      company: {},
      size: 100,
      columns: [
        {
          key: 'code',
          label: 'BL',
          thClass: 'code-header',
          tdClass: 'code-cell',
        },
        {
          key: 'fournisseur',
          label: 'Fournisseur',
          thClass: 'fournisseur-header',
          tdClass: 'fournisseur-cell',
        },
        {
          key: 'destinataire',
          label: 'Destinataire',
          thClass: 'destinataire-header',
          tdClass: 'destinataire-cell',
        },
        {
          key: 'gouvernorat',
          label: 'Adresse',
          thClass: 'gouvernorat-header',
          tdClass: 'gouvernorat-cell',
        },
        {
          key: 'info',
          label: 'Info',
          thClass: 'info-header',
          tdClass: 'info-cell',
        },
        {
          key: 'return_number',
          label: 'NB Retour',
          thClass: 'return-number-header',
          tdClass: 'return-number-cell',
        },
        {
          key: 'prix',
          label: 'Prix',
          thClass: 'prix-header',
          tdClass: 'prix-cell',
        },
        // {
        //   key: 'last_return_reason',
        //   label: 'Motif retour',
        //   thClass: 'return-reason-header',
        //   tdClass: 'return-reason-cell',
        // },
      ],
    }
  },
  computed: {
    publicBaseUrl() {
      return process.env.BASE_URL
    },
  },

  created() {
    this.company = storeAuth.state.company
    this.id = this.$route.params.id
    this.getRoadmap()
  },
  methods: {
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    formatDate(date) {
      if (!date) {
        return null
      }
      // Defensive: ensure date is a string and contains '-'
      if (typeof date !== 'string' || date.indexOf('-') === -1) {
        return date
      }
      const parts = date.split('-')
      if (parts.length < 3) {
        return date
      }
      const [year, month, day] = parts
      const day1 = day.substr(0, 2)
      return `${day1.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
    },
    maskPhone(phone) {
      if (phone === null || phone === undefined || phone === '') {
        return ''
      }

      const value = String(phone)
      let maskedDigits = 0
      let maskedPhone = ''

      for (let i = value.length - 1; i >= 0; i -= 1) {
        const char = value[i]
        if (/\d/.test(char) && maskedDigits < 3) {
          maskedPhone = `*${maskedPhone}`
          maskedDigits += 1
        } else {
          maskedPhone = `${char}${maskedPhone}`
        }
      }

      return maskedPhone
    },

    async getRoadmap() {
      const { data } = await axios.get(`/api/roadmaps/detail/${this.$route.params.id}`)
      this.roadmap = data

      const { orders } = this.roadmap
      const mappedOrders = []
      for (let i = 0; i < orders.length; i += 1) {
        const order = orders[i]
        let dataObj
        if (order.delegation != null) {
          dataObj = {
            code: order.id,
            gouvernorat: order.governorate,
            delegation: order.delegation,
            locality: order.locality,
            fournisseur: order.supplier.username,
            telFournisseur: order.supplier.phone,
            telFournisseur2: order.supplier.phone2,
            tel: order.phone1,
            tel2: order.phone2,
            destinataire: order.name,
            adresse: order.address,
            prix: order.price_ttc,
            date: this.formatDate(order.created_at),
            return_number: order.return_number, // <-- ensure correct mapping
            is_discharge: order.is_discharge,
            is_fragile: order.is_fragile,
            is_an_exchange: order.is_an_exchange,
            can_open_package: order.can_open_package,
            external_supplier_name: order.external_supplier_name,
            external_supplier_phone: order.external_supplier_phone,
            last_return_reason: order.last_return_reason,
          }
        } else {
          dataObj = {
            code: order.id,
            gouvernorat: order.governorate,
            delegation: null,
            locality: null,
            fournisseur: order.supplier.username,
            telFournisseur: order.supplier.phone,
            telFournisseur2: order.supplier.phone2,
            tel: order.phone1,
            tel2: order.phone2,
            destinataire: order.name,
            adresse: order.address,
            prix: order.price_ttc,
            date: this.formatDate(order.created_at),
            return_number: order.return_number, // <-- ensure correct mapping
            is_discharge: order.is_discharge,
            is_fragile: order.is_fragile,
            is_an_exchange: order.is_an_exchange,
            can_open_package: order.can_open_package,
            external_supplier_name: order.external_supplier_name,
            external_supplier_phone: order.external_supplier_phone,
            last_return_reason: order.last_return_reason,
          }
        }
        mappedOrders.push(dataObj)
      }
      this.orders = mappedOrders // <-- assign array at once for reactivity
      this.total = this.roadmap.orders.reduce((accumulator, item) => accumulator + Number(item.price_ttc), 0)
    },
    async sortByGovernorate() {
      try {
        // Sort orders by governorate, then delegation, then locality
        const sortedOrders = [...this.orders].sort((a, b) => {
          // Compare governorates first
          const govA = a.gouvernorat || ''
          const govB = b.gouvernorat || ''
          const govCompare = govA.localeCompare(govB, 'fr', { sensitivity: 'base' })

          if (govCompare !== 0) {
            return govCompare
          }

          // If governorates are the same, compare delegations
          const delA = a.delegation || ''
          const delB = b.delegation || ''
          const delCompare = delA.localeCompare(delB, 'fr', { sensitivity: 'base' })

          if (delCompare !== 0) {
            return delCompare
          }

          // If delegations are the same, compare localities (if available)
          // Note: locality might not be in the mapped data, so we handle it gracefully
          const locA = a.locality || ''
          const locB = b.locality || ''
          return locA.localeCompare(locB, 'fr', { sensitivity: 'base' })
        })

        // Update the orders display
        this.orders = sortedOrders

        // Extract order IDs in the new sorted order
        const orderedIds = sortedOrders.map(order => order.code)

        // Send the new order to the backend
        await axios.post(`/api/roadmaps/sort-orders/${this.$route.params.id}/`, {
          order_ids: orderedIds,
        })

        // Show success message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Succès',
            icon: 'CheckIcon',
            text: 'Les colis ont été triés par localisation (gouvernorat, délégation, localité)',
            variant: 'success',
          },
        })
      } catch (error) {
        console.error('Error sorting orders:', error)
        const errorMessage = error.response?.data?.error || 'Erreur lors du tri des colis'
        const debugInfo = error.response?.data?.debug

        if (debugInfo) {
          console.log('Debug info:', debugInfo)
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            icon: 'AlertTriangleIcon',
            text: errorMessage,
            variant: 'danger',
          },
        })
      }
    },
  },
  setup() {
    const invoiceData = ref(null)
    const paymentDetails = ref({})
    const sommeFrais = 0
    const total = 0
    const tax = 0
    const sommeHT = 0
    const printInvoice = () => {
      window.print()
    }

    return {
      invoiceData,
      paymentDetails,
      printInvoice,
      sommeHT,
      tax,
      total,
      sommeFrais,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";

// Cards styling
.cards-container {
  gap: 20px;

  .info-card {
    border: 2px solid black;
    padding: 20px;
    background: white;
    flex: 1;

    .h3 {
      margin-bottom: 10px;
      font-size: 1.7rem; // Increased font size
      font-weight: bold;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .company-card {
    margin-right: 10px;
  }

  .delivery-card {
    margin-left: 10px;
  }
}

table,
td,
th {
  text-align: left;
  border: 1px solid black;
}

footer {
  bottom: 0;
  height: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

.mycolor {
  background-color: #EA2F2F;
}

.contact {
  background-color: #EA2F2F;
  padding: 10px;
  border-radius: 20px;
}

// Accessibilité : focus visible pour les champs interactifs
input:focus, select:focus, .v-select:focus {
  outline: 2px solid #005fcc !important;
  outline-offset: 2px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}
</style>

<style lang="scss">
@media print {
  @page {
    size: A2 landscape;
    margin: 8mm;
  }

  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }

  nav.header-navbar,
  .main-menu,
  .header-navbar-shadow,
  footer.footer,
  .customizer-toggle,
  .invoice-actions {
    display: none !important;
  }

  .app-content,
  .content,
  .content-wrapper,
  .content-body,
  .content.app-content {
    margin-left: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .invoice-preview-wrapper,
  .invoice-preview {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 0 6mm !important;
    box-sizing: border-box !important;
    display: block !important;
  }

  .invoice-preview > .col-12,
  .invoice-preview > .col-md-8,
  .invoice-preview > .col-xl-9,
  .invoice-preview > [class*="col-"] {
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 100% !important;
    padding: 0 !important;
  }

  .table-responsive {
    overflow: visible !important;
  }

  .invoice-preview-card {
    width: 100% !important;
    max-width: 100% !important;
    border: 0 !important;
    box-shadow: none !important;
    margin: 0 !important;
  }

  .invoice-preview-card .card-body {
    padding: 8px 0 !important;
  }

  .invoice-preview-card h1 {
    font-size: 24px !important;
    line-height: 1.2 !important;
    margin: 0 !important;
  }

  .invoice-preview-card h4 {
    font-size: 18px !important;
    line-height: 1.2 !important;
    margin: 0 !important;
  }

  .cards-container {
    gap: 14px !important;
    margin-bottom: 12px !important;
  }

  .cards-container .info-card {
    border: 1px solid #000 !important;
    padding: 8px !important;
  }

  .cards-container .info-card .h3 {
    font-size: 12px !important;
    line-height: 1.3 !important;
    margin: 0 0 3px !important;
  }

  .ta {
    width: 100% !important;
    table-layout: auto !important;
    border-collapse: collapse !important;
    font-size: 13px !important;
    color: #000 !important;
  }

  .ta th,
  .ta td {
    border: 1px solid #000 !important;
    padding: 4px 5px !important;
    vertical-align: top !important;
    line-height: 1.25 !important;
    white-space: normal !important;
    word-break: normal !important;
    overflow-wrap: break-word !important;
  }

  .ta th {
    font-size: 12px !important;
    text-transform: uppercase !important;
    text-align: center !important;
    white-space: nowrap !important;
    letter-spacing: 0 !important;
  }

  .code-header,
  .code-cell {
    width: 1% !important;
    text-align: center !important;
    white-space: nowrap !important;
  }

  .fournisseur-header,
  .fournisseur-cell {
    width: auto !important;
  }

  .destinataire-header,
  .destinataire-cell {
    width: auto !important;
  }

  .gouvernorat-header,
  .gouvernorat-cell {
    width: auto !important;
  }

  .info-header,
  .info-cell {
    width: 1% !important;
    text-align: center !important;
    white-space: nowrap !important;
  }

  .return-number-header,
  .return-number-cell {
    width: 1% !important;
    text-align: center !important;
    white-space: nowrap !important;
  }

  .prix-header,
  .prix-cell {
    width: 1% !important;
    white-space: nowrap !important;
  }

  .fournisseur-cell strong,
  .destinataire-cell strong,
  .gouvernorat-cell strong {
    display: block !important;
    font-size: 13px !important;
    line-height: 1.3 !important;
    margin: 0 0 2px !important;
    text-align: left !important;
  }

  .fournisseur-cell strong:first-child,
  .destinataire-cell strong:first-child {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .info-cell span,
  .return-number-cell span,
  .prix-cell span,
  .code-cell span {
    font-size: 13px !important;
    font-weight: 700 !important;
  }

  .prix-cell {
    text-align: right !important;
  }

  .prix-cell span[style*="font-size: 30px"],
  .prix-cell span[style*="font-size:30px"] {
    font-size: 12px !important;
    margin-top: 2px !important;
    text-align: left !important;
  }

  .d-flex.justify-content-between.mt-5 h3,
  .d-flex.justify-content-between.mt-5 h1 {
    font-size: 18px !important;
    margin: 0 !important;
    line-height: 1.2 !important;
  }

  .ta tr {
    page-break-inside: avoid !important;
  }

  * {
    color: #000 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}

// Screen styles for better development view
@media screen {
  .ta {
    .code-cell, .fournisseur-cell {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 6px 0;
      min-height: 50px;
    }
    .fournisseur-cell {
      min-height: 50px;
    }

    .prix-cell {
      text-align: right;
    }

    .fournisseur-cell, .destinataire-cell, .gouvernorat-cell {
      strong {
        display: block;
        margin-bottom: 4px;
      }
    }
  }
}
</style>
